import { useSearchPageInteraction, useSearchPageInteractionStore } from "@/providers/search";
import { useOptimizedClicked } from "@/providers/search/hooks/use-optimized-clicked";
import { usePropertyInteractionStore } from "@/stores/property-interaction";
import { MapItemSUP } from "@/types/search";
import { cn } from "@/utils/cn";
import React from "react";
import { SUPCard } from "../../sup/card";

export interface Project {
	projectId?: string;
	name?: string;
	sellerProjects?: string[];
	units?: string[];
	handout?: boolean;
}

interface SearchItemProps {
	data: MapItemSUP;
	rank: number;
	disableHighlight?: boolean;
	disableOnClick?: boolean;
	hChecked?: ((props: { id: string }) => void) | undefined;
	checked?: boolean;
	onToggleFavorite?: (property: MapItemSUP) => void;
	favorites?: MapItemSUP[];
	className?: string;
}

// Priority loading for first 4 items (2x2 grid on desktop, or first 4 on mobile) to improve LCP
const LCP_PRIORITY_THRESHOLD = 4;

export const SearchItem = React.memo(function SearchItem({
	data,
	rank,
	disableHighlight,
	disableOnClick,
	className,
}: SearchItemProps) {
	const {
		gps,
		coordinates: c,
		mapCoordinates,
		type: _type,
	} = data as MapItemSUP & { mapCoordinates?: [number, number] | string };

	const coordinates: [number, number] | undefined = React.useMemo(() => {
		// Priority: mapCoordinates (cluster/marker location), then GPS coordinates, then original coordinates
		// We prioritize mapCoordinates to ensure the card highlights the correct marker on the map
		if (mapCoordinates) {
			if (Array.isArray(mapCoordinates)) return mapCoordinates;
			// Use string coordinates if available (common for map clusters)
			if (typeof mapCoordinates === "string") {
				const [lngStr, latStr] = mapCoordinates.split(",").map((s: string) => s.trim());
				const lng = Number.parseFloat(lngStr ?? "");
				const lat = Number.parseFloat(latStr ?? "");

				if (Number.isFinite(lng) && Number.isFinite(lat)) {
					return [lng, lat];
				}
			}
		}
		if (gps) return [gps.lng, gps.lat];
		return c;
	}, [gps, mapCoordinates, c]);
	const coordinatesString = React.useMemo(() => {
		if (!coordinates) return "";
		// Ensure consistent string format regardless of input type
		if (Array.isArray(coordinates)) {
			return coordinates.toString();
		}
		// If coordinates is already a string, use it directly
		if (typeof coordinates === "string") return coordinates;
		return "";
	}, [coordinates]);

	// Zero-rerender hover system using refs
	const elementRef = React.useRef<HTMLDivElement | null>(null);
	const isHoveredRef = React.useRef(false);
	const isGloballyHoveredRef = React.useRef(false);

	// Use optimized click state only (doesn't cause re-renders during hover)
	const { isClicked, handleToggleClick } = useOptimizedClicked(coordinatesString);

	// Create unique hover key combining property ID and coordinates
	const hoverKey = React.useMemo(() => `${data._id}-${coordinatesString}`, [data._id, coordinatesString]);

	// Get store for manual subscription
	const interactionStore = useSearchPageInteractionStore();
	// Get property overlay hook
	const setHoveredMarkerId = useSearchPageInteraction(state => state.setHoveredMarkerId);

	// Direct DOM manipulation for hover effects
	const applyHoverEffects = React.useCallback((isHovered: boolean) => {
		if (elementRef.current) {
			const element = elementRef.current;
			if (isHovered) {
				element.classList.add("shadow-xl", "scale-[1.02]");
			} else {
				element.classList.remove("shadow-xl", "scale-[1.02]");
			}
		}
	}, []);

	// Zero-rerender hover handlers
	const handleMouseEnter = React.useCallback(() => {
		isHoveredRef.current = true;
		applyHoverEffects(true);
		// Update global state for cross-component sync - use unique ID to prevent all items from hovering
		setHoveredMarkerId(hoverKey);
	}, [applyHoverEffects, hoverKey, setHoveredMarkerId]);

	const handleMouseLeave = React.useCallback(() => {
		isHoveredRef.current = false;
		applyHoverEffects(false);
		// Clear global state
		setHoveredMarkerId(null);
	}, [applyHoverEffects, setHoveredMarkerId]);

	// Listen for global hover changes without causing re-renders
	React.useEffect(() => {
		const unsubscribe = interactionStore.subscribe(state => {
			const isGloballyHovered = state.hoveredMarkerId === hoverKey;

			if (isGloballyHovered !== isGloballyHoveredRef.current) {
				isGloballyHoveredRef.current = isGloballyHovered;
				applyHoverEffects(isHoveredRef.current || isGloballyHoveredRef.current);
			}
		});

		// Initial check
		const state = interactionStore.getState();
		const isGloballyHovered = state.hoveredMarkerId === hoverKey;
		if (isGloballyHovered !== isGloballyHoveredRef.current) {
			isGloballyHoveredRef.current = isGloballyHovered;
			applyHoverEffects(isHoveredRef.current || isGloballyHoveredRef.current);
		}

		return unsubscribe;
	}, [interactionStore, hoverKey, applyHoverEffects]);

	// Check if this item is highlighted (clicked) - this is fine to cause re-renders since it's a deliberate user action
	const isHighlighted = React.useMemo(() => {
		return !disableHighlight && isClicked;
	}, [disableHighlight, isClicked]);

	// Find all properties at the same coordinates from map data
	const propertiesAtCoordinates = React.useMemo(() => {
		// Use pre-populated relatedSups if available (avoids subscribing to full mapData)
		if (data.relatedSups && data.relatedSups.length > 0) {
			return data.relatedSups;
		}

		return [data];
	}, [data]);

	// Memoize the click handler to prevent recreation on every render
	const openPropertyOverlay = usePropertyInteractionStore(state => state.openPropertyOverlay);

	const handleClick = React.useCallback(
		(e: React.MouseEvent) => {
			if (disableOnClick) return;
			e.stopPropagation();
			if (!coordinatesString || !coordinates) return;

			// Open property overlay with all properties at this coordinate using new system
			const initialSupId = data._id;

			// Use the new PropertyOverlay system via interaction store
			openPropertyOverlay({ sups: propertiesAtCoordinates, initialSupId, projectName: data.projectName || "" });

			// Toggle selection using optimized hook with proper marker data
			handleToggleClick({
				coordinates,
				projectId: data.projectId || "",
				projectName: data.projectName || "",
			});

			// Don't center map on click to avoid conflicts with marker behavior
		},
		[
			disableOnClick,
			coordinatesString,
			coordinates,
			handleToggleClick,
			data,
			propertiesAtCoordinates,
			openPropertyOverlay,
		],
	);

	return (
		<>
			<div
				ref={elementRef}
				className={cn(
					"search-item-card",
					"flex w-full",
					"gap-0.5",
					"cursor-pointer",
					"transition-all duration-300",
					"min-h-[120px]",
					"bg-white",
					"rounded-2xl overflow-hidden",
					className,
				)}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onClick={handleClick}
			>
				<SUPCard
					data={data}
					className={`${isHighlighted ? "shadow-xl" : ""}`}
					shouldPrioritizeCoverImage={rank <= LCP_PRIORITY_THRESHOLD}
				/>
			</div>
		</>
	);
});
